import React, { useState } from "react";
import { App } from "antd";
import axios from "axios";
import "../QRLink/QRLink.css";
import PageHeader from "../../components/PageHeader";
import HtmlEditor from "../../components/HtmlEditor";
import OfficePdfResultActions from "../../components/OfficePdfResultActions";
import ConversionSuccessModal from "../../components/ConversionSuccessModal";
import { FileTextOutlined, FilePdfOutlined, ReloadOutlined } from "@ant-design/icons";

const MAX_CONTENT_LENGTH = 2000000;

const stripHtml = (html = "") => html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();

const stripFileNamePrefix = (fileName = "") => {
  const separatorIndex = fileName.indexOf("_");
  return separatorIndex >= 0 ? fileName.slice(separatorIndex + 1) : fileName;
};

const TextToPdf = () => {
  const { message } = App.useApp();
  const [title, setTitle] = useState("");
  const [html, setHtml] = useState("");
  const [converting, setConverting] = useState(false);
  const [result, setResult] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const isModule = window.location.pathname.includes("/dashboard/");

  const handleConvert = async () => {
    if (!title.trim()) {
      message.warning("Please enter a document name.");
      return;
    }

    if (!stripHtml(html)) {
      message.warning("Please enter some text content.");
      return;
    }

    if (html.length > MAX_CONTENT_LENGTH) {
      message.error("Content is too large.");
      return;
    }

    setConverting(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("Content", html);
      formData.append("Source", isModule ? "Web" : "Public-Web");
      formData.append("FileName", title.trim());

      const token = localStorage.getItem("token") || "";
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/Pdf/richtext-to-pdf`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const data = response.data?.data;

      if (!response.data?.error && data?.downloadUrl) {
        setResult({
          downloadUrl: data.downloadUrl,
          fileName: stripFileNamePrefix(data.fileName) || `${title.trim() || "Document"}.pdf`,
          outputType: "pdf",
        });
        setShowSuccessModal(true);
      } else {
        message.error(response.data?.message || "Conversion failed.");
      }
    } catch (error) {
      console.error(error);
      message.error(error?.response?.data?.message || "Conversion failed. Please try again.");
    } finally {
      setConverting(false);
    }
  };

  const reset = () => {
    setTitle("");
    setHtml("");
    setResult(null);
    setShowSuccessModal(false);
  };

  return (
    <>
      <PageHeader />

      <div className={`qrl-root ${isModule ? "qrl-module" : ""}`}>
        <div className="qrl-inner">
          <div className="qrl-form-side">
            <div className="qrl-heading">
              <div className="qrl-heading-icon">
                <FileTextOutlined />
              </div>
              <div>
                <h1 className="qrl-title">Text to PDF</h1>
                <p className="qrl-subtitle">
                  Type or paste your text, format it, and turn it into a PDF.
                </p>
              </div>
            </div>

            <div className="qrl-card">
              <div className="qrl-field">
                <label className="qrl-label">
                  Document Name <span style={{ color: "#e5322d" }}>*</span>
                </label>
                <input
                  className="qrl-input"
                  placeholder="e.g. Meeting Notes"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  required
                />
              </div>

              <div className="qrl-field">
                <label className="qrl-label">Text Content</label>
                <HtmlEditor value={html} onChange={setHtml} placeholder="Write your text here" />
              </div>

              <div className="qrl-divider" />

              <div className="qrl-actions">
                <button
                  className="qrl-generate-btn"
                  onClick={handleConvert}
                  disabled={converting}
                  type="button"
                >
                  {converting ? <span className="qrl-spinner" /> : <FilePdfOutlined />}
                  {converting ? "Converting..." : "Convert to PDF"}
                </button>

                {result && (
                  <button className="qrl-reset-btn" onClick={reset} type="button">
                    <ReloadOutlined /> Start over
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="qrl-preview-side">
            <div className="qrl-preview-card">
              <div className="qrl-preview-label">PDF Preview</div>

              {result ? (
                <OfficePdfResultActions
                  downloadUrl={result.downloadUrl}
                  originalFileName={result.fileName}
                  outputType={result.outputType}
                  requiresAuth={isModule}
                  stacked
                />
              ) : (
                <div className="qrl-placeholder">
                  <div className="qrl-placeholder-icon">
                    <FilePdfOutlined />
                  </div>
                  <div className="qrl-placeholder-text">
                    Fill in your text and click<br />
                    <strong>Convert to PDF</strong>
                  </div>
                </div>
              )}
            </div>

            <div className="qrl-tips-card">
              <div className="qrl-tips-title">Tips</div>
              <ul className="qrl-tips-list">
                <li>Use the toolbar to bold, underline, or list key points</li>
                <li>Give your document a name for an easy-to-find file</li>
                <li>Keep formatting simple for the cleanest PDF layout</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <ConversionSuccessModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        fileName={result?.fileName}
        downloadUrl={result?.downloadUrl}
        outputType={result?.outputType}
        requiresAuth={isModule}
        subtitle="Your text has been converted to PDF successfully."
      />
    </>
  );
};

export default TextToPdf;
